import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, skipToken } from '@tanstack/react-query';
import { applicationQueryKey } from '../../queries/applicationQueries';
import { ApplicationParameters } from '../types/ApplicationParameters';

/**
 * Attribute stamped on <html> once a print route has finished rendering.
 * The Functions app (IronPdfService.PrintReadySelector) waits for it before printing the PDF.
 * Value is 'true' when the application loaded, 'error' when the load failed.
 */
export const PRINT_READY_ATTRIBUTE = 'data-print-ready';

/** Time to let post-data effects (cache writes, re-renders) settle before stamping. */
const SETTLE_MS = 300;

declare global {
    interface Window {
        /** Injected by IronPdf's Chrome when rendering with WaitFor.JavaScript; absent everywhere else. */
        ironpdf?: { notifyRender?: () => void };
    }
}

/**
 * In print mode, marks the document as ready for PDF capture once the application query has
 * settled, web fonts have loaded, and the page has had a moment to paint.
 * Does nothing outside print mode.
 */
export const usePrintReady = (printMode: boolean) => {
    const { entryGuid } = useParams<ApplicationParameters>();
    const { status } = useQuery({
        queryKey: applicationQueryKey(entryGuid ?? ''),
        queryFn: skipToken,
    });

    useEffect(() => {
        if (!printMode || status === 'pending') {
            return;
        }

        let cancelled = false;
        let settleTimer: number | undefined;
        let frame: number | undefined;

        const stamp = () => {
            if (cancelled) return;
            document.documentElement.setAttribute(PRINT_READY_ATTRIBUTE, status === 'error' ? 'error' : 'true');
            window.ironpdf?.notifyRender?.();
        };

        const fontsReady: Promise<unknown> = document.fonts?.ready ?? Promise.resolve();
        fontsReady.then(() => {
            if (cancelled) return;
            settleTimer = window.setTimeout(() => {
                // Two frames so layout and paint of the settled DOM have both happened.
                frame = requestAnimationFrame(() => {
                    frame = requestAnimationFrame(stamp);
                });
            }, SETTLE_MS);
        });

        return () => {
            cancelled = true;
            if (settleTimer !== undefined) window.clearTimeout(settleTimer);
            if (frame !== undefined) cancelAnimationFrame(frame);
            document.documentElement.removeAttribute(PRINT_READY_ATTRIBUTE);
        };
    }, [printMode, status]);
};
